import apiClient from './client';
import { tokenStorage, ImpersonatorInfo } from '@/lib/token';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refresh_token: string;
}

export interface UserRole {
  id: number;
  code: string;
  name: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  fullname: string;
  roles: string[];
  roleName: string;
  role: UserRole | null;
  permissions: string[];
  favoriteSchoolIds: number[];
}

export interface ImpersonateResponse {
  token: string;
  refresh_token: string;
  impersonating: {
    id: number;
    username: string;
    fullname: string;
  };
  impersonator: {
    id: number;
    username: string;
    fullname: string;
  };
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>('/api/auth/login', credentials);
    const { token, refresh_token } = response.data;
    tokenStorage.setTokens(token, refresh_token);
    return response.data;
  },

  logout: async (): Promise<void> => {
    const refreshToken = tokenStorage.getRefreshToken();
    try {
      await apiClient.post('/api/auth/logout', { refresh_token: refreshToken });
    } finally {
      tokenStorage.clearTokens();
    }
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/api/auth/me');
    return response.data;
  },

  refreshToken: async (): Promise<LoginResponse> => {
    const refreshToken = tokenStorage.getRefreshToken();
    const response = await apiClient.post<LoginResponse>('/api/auth/refresh', {
      refresh_token: refreshToken,
    });
    const { token, refresh_token } = response.data;
    tokenStorage.setTokens(token, refresh_token);
    return response.data;
  },

  /**
   * Impersonate another user (Super Admin only)
   * Stores current tokens and switches to target user's session
   */
  impersonate: async (userId: number): Promise<ImpersonateResponse> => {
    // Store current tokens before impersonation
    tokenStorage.storeOriginalTokens();

    const response = await apiClient.post<ImpersonateResponse>(`/api/auth/impersonate/${userId}`);
    const { token, refresh_token } = response.data;

    // Set the impersonated user's tokens
    tokenStorage.setTokens(token, refresh_token);

    return response.data;
  },

  /**
   * Exit impersonation and restore original user session
   */
  exitImpersonation: async (): Promise<boolean> => {
    const restored = tokenStorage.restoreOriginalTokens();
    return restored;
  },

  /**
   * Check if currently impersonating another user
   */
  isImpersonating: (): boolean => {
    return tokenStorage.isImpersonating();
  },

  /**
   * Get impersonator info if currently impersonating
   */
  getImpersonator: (): ImpersonatorInfo | null => {
    return tokenStorage.getImpersonator();
  },
};
